import { API_ENDPOINTS } from '../config/api';

export interface CheckoutInitRequest {
  plan_id?: string;
  currency?: string;
  billing_cycle?: string;
}

export interface CheckoutInitResponse {
  success: boolean;
  message?: string;
  checkout_token?: string;
  session_id?: string;
  public_key?: string;
  product_id?: string;
  plan_id?: string;
  sandbox?: boolean;
  sandbox_token?: string;
  sandbox_timestamp?: number;
}

export interface CancelSubscriptionRequest {
  subscription_id: string;
}

export interface TransactionResponse {
  success: boolean;
  message?: string;
  transaction?: {
    id: string;
    status: string;
    user_email: string;
    plan_id: string;
    amount?: number;
    currency?: string;
    created_at: string;
    completed_at?: string;
    subscription_id?: string;
  };
}

declare global {
  interface Window {
    FS?: any;
    FSCheckout?: any;
  }
}

export const paymentService = {
  async initiateCheckout(token: string, request: CheckoutInitRequest = {}): Promise<CheckoutInitResponse> {
    try {
      const response = await fetch(API_ENDPOINTS.PAYMENT.CHECKOUT_INIT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          billing_cycle: request.billing_cycle || 'monthly',
          currency: request.currency || 'USD',
          plan_id: request.plan_id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error initiating checkout:', error);
      return {
        success: false,
        message: 'Failed to initiate checkout',
      };
    }
  },

  async cancelSubscription(token: string, subscriptionId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(API_ENDPOINTS.PAYMENT.CANCEL_SUBSCRIPTION, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription_id: subscriptionId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      return {
        success: false,
        message: 'Failed to cancel subscription',
      };
    }
  },

  async getTransaction(token: string, transactionId: string): Promise<TransactionResponse> {
    try {
      const response = await fetch(`${API_ENDPOINTS.PAYMENT.TRANSACTION}?id=${transactionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching transaction:', error);
      return {
        success: false,
        message: 'Failed to fetch transaction details',
      };
    }
  },

  async loadFreemiusCheckout(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.FS && window.FS.Checkout) {
        resolve();
        return;
      }

      // Create script element - Freemius v1 API
      const script = document.createElement('script');
      script.src = 'https://checkout.freemius.com/js/v1/';
      script.async = true;

      script.onload = () => {
        // Wait for FS to be available
        let attempts = 0;
        const checkInterval = setInterval(() => {
          attempts++;
          if (window.FS && window.FS.Checkout) {
            clearInterval(checkInterval);
            resolve();
          } else if (attempts > 20) { // 2 seconds timeout
            clearInterval(checkInterval);
            reject(new Error('Freemius checkout failed to initialize'));
          }
        }, 100);
      };

      script.onerror = () => {
        reject(new Error('Failed to load Freemius checkout script'));
      };

      document.head.appendChild(script);
    });
  },

  async openCheckoutModal(
    checkoutData: CheckoutInitResponse,
    onSuccess?: (data: any) => void,
    onCancel?: () => void
  ): Promise<void> {
    if (!checkoutData.public_key || !checkoutData.product_id || !checkoutData.checkout_token) {
      throw new Error('Missing required checkout data');
    }

    try {
      // Load Freemius script
      await this.loadFreemiusCheckout();

      if (!window.FS || !window.FS.Checkout) {
        throw new Error('Freemius checkout not available');
      }

      // Create checkout instance according to Freemius docs
      console.log('Full checkout data received from backend:', checkoutData);
      console.log('Creating checkout with:', {
        product_id: checkoutData.product_id,
        plan_id: checkoutData.plan_id,
        public_key: checkoutData.public_key,
      });

      // Configure sandbox mode properly
      const checkoutConfig: any = {
        product_id: checkoutData.product_id,
        plan_id: checkoutData.plan_id,
        public_key: checkoutData.public_key,
      };

      // In sandbox mode, pass token and context timestamp
      if (checkoutData.sandbox && checkoutData.sandbox_token && checkoutData.sandbox_timestamp) {
        checkoutConfig.sandbox = {
          token: checkoutData.sandbox_token,
          ctx: checkoutData.sandbox_timestamp
        };
        console.log('Using sandbox mode with token:', checkoutData.sandbox_token, 'timestamp:', checkoutData.sandbox_timestamp);
      } else if (checkoutData.sandbox) {
        // Fallback for backward compatibility
        checkoutConfig.sandbox = true;
        console.log('Using sandbox mode (boolean fallback)');
      }

      const checkout = new window.FS.Checkout(checkoutConfig);

      // Open checkout with user token
      checkout.open({
        user_token: checkoutData.checkout_token,
        success: (response: any) => {
          console.log('Payment successful:', response);
          if (onSuccess) {
            onSuccess(response);
          }
        },
      });

    } catch (error) {
      console.error('Error opening checkout modal:', error);
      throw error;
    }
  },
};